import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";

// ============================================================
// SearchPicker — typeahead input that opens a modal with a
// search-driven list. For dropdowns with many entries (centenas
// de marcas/modelos/variantes/cidades).
//
// Props:
//   label          — label rendered above the trigger button
//   value          — currently selected raw item (or id)
//   selectedLabel  — string to show when collapsed
//   placeholder    — placeholder shown when nothing selected
//   disabled       — disables the trigger
//   fetchFn        — async (search) => { data: any[] } — server call
//   itemKey        — (item) => unique key
//   itemLabel      — (item) => display label
//   onPick         — (item) => void
//   noResultsText  — fallback when fetch returns empty
// ============================================================

const SearchPicker = ({
  label,
  selectedLabel,
  placeholder,
  disabled,
  fetchFn,
  itemKey,
  itemLabel,
  onPick,
  noResultsText = "Sem resultados",
  modalTitle,
  searchPlaceholder = "Pesquisar...",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const load = (q) => {
    if (!fetchFn) return;
    setLoading(true);
    Promise.resolve(fetchFn(q || ""))
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? [];
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setItems([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!open) return;
    setSearch("");
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSearch = (v) => {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(v), 280);
  };

  const onSelect = (it) => {
    onPick(it);
    setOpen(false);
  };

  return (
    <>
      {label && (
        <label className="form-label" style={{ fontSize: 13 }}>
          {label}
        </label>
      )}
      <button
        type="button"
        className={`form-control text-start d-flex align-items-center justify-content-between ${
          disabled ? "bg-light" : ""
        }`}
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          color: selectedLabel ? "#212529" : "#adb5bd",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {selectedLabel || placeholder || "—"}
        </span>
        <i className="bi bi-chevron-down ms-2" />
      </button>

      <Modal isOpen={open} toggle={() => setOpen(false)} centered size="md">
        <ModalHeader toggle={() => setOpen(false)}>
          {modalTitle || label || "Selecionar"}
        </ModalHeader>
        <ModalBody>
          <input
            className="form-control mb-2"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            autoFocus
          />
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {loading && (
              <div className="text-center text-muted py-3">
                <i className="bi bi-arrow-clockwise" /> ...
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="text-center text-muted py-3">
                {noResultsText}
              </div>
            )}
            {!loading &&
              items.map((it) => (
                <button
                  key={itemKey(it)}
                  type="button"
                  className="list-group-item list-group-item-action text-start w-100 d-flex align-items-center"
                  onClick={() => onSelect(it)}
                  style={{
                    border: 0,
                    borderBottom: "1px solid #e9ecef",
                    padding: "10px 12px",
                  }}
                >
                  <span className="flex-grow-1">{itemLabel(it)}</span>
                </button>
              ))}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default SearchPicker;
